import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/auth/Login';
import Register from './components/auth/Register';  
import Dashboard from './components/dashboard/EnhancedDashboard';
import DashboardLayout from './components/layout/DashboardLayout';
import { ThemeProvider } from './components/ThemeContext';
import TestList from './components/tests/TestList';
import PerformanceOverview from './components/dashboard/PerformanceOverview';
import TakeTest from './components/tests/TakeTest';
import TestResult from './components/results/TestResult';
import ForgotPassword from './components/auth/ForgotPassword';
import ChatBot from './components/ai/ChatBot';
import Settings from './components/settings/Settings';
import LoginOTP from './components/auth/LoginOTP'; 
import SuperAdminUserManagement from './components/UserManagement/SuperAdminUserManagement';
import CoachingAdminUserManagement from './components/UserManagement/CoachingAdminUserManagement';
import BatchManagement from './components/BatchManagement/BatchManagement'; // Import BatchManagement component
import MaterialsManager from './components/materials/MaterialsManager';
import MaterialsCRUD from './components/crud/MaterialsCRUD';

// Import other components

// Configure axios defaults
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.withCredentials = true; // Enable sending cookies with requests

// Add request interceptor
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

   // Function to check if a user has the required role for a route
   const hasRequiredRole = (requiredRoleId) => {
    const userRoleId = localStorage.getItem('userRole');
    return userRoleId === requiredRoleId.toString();
  };

  // Route protection component
  const ProtectedRoute = ({ children, requiredRoles = [] }) => {
    const token = localStorage.getItem('token');
    const userRoleId = localStorage.getItem('userRole');
    
    // Always check localStorage directly for the token
    if (!token) {
      return <Navigate to="/login" />;
    }
    
    // Check role-based access
    if (requiredRoles.length > 0) {
      const hasRole = requiredRoles.some(role => userRoleId === role.toString());
      if (!hasRole) {
        return <div>Unauthorized access</div>;
      }
    }
    
    return children;
  };


  
  const userRoles = {
    superAdmin: 4,
    coachingAdmin: 1
  };

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const res = await axios.get('/api/auth/user');
          setUser(res.data);
          setIsAuthenticated(true);
        } catch (err) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Login function
  const [loginLoading, setLoginLoading] = useState(false);

  const login = async (email, password) => {
    if (loginLoading) return;  // Prevent duplicate calls
    setLoginLoading(true);
    try {
      const res = await axios.post('/api/auth/login', { email, password });
      const { token, user } = res.data;
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Set user and authentication state
      setUser(user);
      setUserRole(user.role_id); // Store role in application state
      localStorage.setItem('userRole', user.role_id.toString()); // Store role in localStorage
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        message: err.response?.data?.message || 'Login failed. Please try again.'
      };
    } finally {
      setLoginLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const res = await axios.post('/api/auth/register', userData);
      const { token, user } = res.data;
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Set user and authentication state
      setUser(user);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        message: err.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <Login login={login} isAuthenticated={isAuthenticated} />
          } />
          <Route path="/register" element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <Register register={register} isAuthenticated={isAuthenticated} />
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/login-otp" element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <LoginOTP />
          } />


          
          {/* Protected routes with layout */}
          <Route element={
                <ProtectedRoute>
                  <DashboardLayout user={user} />
                </ProtectedRoute>
              }>
            
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard user={user} /></ProtectedRoute>} />
              <Route path="/tests" element={<TestList user={user} />} />
              <Route path="/performance" element={<PerformanceOverview />} />
              <Route path="/take-test/:id" element={<TakeTest user={user} />} />
              <Route path="/ai-assistant" element={<ChatBot />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/materials" element={<MaterialsManager />} />
              <Route path="/crud/materials" element={<ProtectedRoute requiredRoles={[1, 4]}><MaterialsCRUD /></ProtectedRoute>} />
              {/* Redirect old lectures route to materials */}
              <Route path="/lectures" element={<Navigate to="/materials" replace />} />
              {/* Add other protected routes here */}
              <Route path="/results/:id" element={<TestResult />} />
          </Route>
              {/* Super Admin Route */}
              <Route path="/admin/users" element={<ProtectedRoute requiredRoles={[userRoles.superAdmin]}><SuperAdminUserManagement /></ProtectedRoute>} />
              {/* Coaching Admin Route */}
              <Route path="/coaching/users" element={<ProtectedRoute requiredRoles={[userRoles.coachingAdmin]}><CoachingAdminUserManagement /></ProtectedRoute>} />
              <Route path="/batch-management" element={<ProtectedRoute requiredRoles={[userRoles.coachingAdmin, userRoles.superAdmin]}><BatchManagement /></ProtectedRoute>} /> {/* Add Batch Management Route */}
          
              <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
